import { useMemo, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import starExp from '~/assets/icons/star_exp.svg';
import {
	ClothesImage,
	CollectionImage,
	CurrencyIcon,
} from '~/components/collections';
import { useWalletIcon } from '~/hooks';
import { rage, useObservable } from '~/rage';
import {
	Currency,
	isClothesItem,
	ParseRewardValueTypeMap,
	RewardRaw,
	RewardType,
	parseRewardString,
} from '~/shared';
import cardSoft from '../../assets/images/cases/card_soft.png';
import { TextLabel } from '../TextLabel';
import {
	StyledContainer,
	StyledHintContainer,
	StyledItemContainer,
	StyledShadow,
} from './QuestPromocodeItem.styled';

const tryParseReward = <T extends RewardType>(
	type: T,
	value: string,
): ParseRewardValueTypeMap[T] | null => {
	try {
		return parseRewardString(type, value);
	} catch {
		return null;
	}
};

const QuestPromocodeItem = ({
	item,
	rowSpan,
	colSpan,
}: {
	item: RewardRaw;
	rowSpan?: number;
	colSpan?: number;
}) => {
	const [hintVisible, setHintVisible] = useState(false);
	const rubleIcon = useWalletIcon(Currency.Ruble, true);
	const lootboxes = useObservable(rage.cases.getLootboxes());
	const bundles = useObservable(rage.cases.getBundles());
	const inventoryItems = useObservable(rage.inventory.getInventoryItems());

	const isBig = rowSpan === 2 && colSpan === 2;
	const collectionSize = isBig ? 100 : 50;
	const iconSize = isBig ? 60 : 30;

	const parsedVehicle = useMemo(
		() =>
			item.type === RewardType.VEHICLE
				? tryParseReward(RewardType.VEHICLE, item.value)
				: null,
		[item],
	);

	const parsedItem = useMemo(
		() =>
			item.type === RewardType.ITEM
				? tryParseReward(RewardType.ITEM, item.value)
				: null,
		[item],
	);

	const lootboxId = useMemo(
		() =>
			item.type === RewardType.LOOTBOX
				? tryParseReward(RewardType.LOOTBOX, item.value)
				: null,
		[item],
	);

	const bundleId = useMemo(
		() =>
			item.type === RewardType.BUNDLE
				? tryParseReward(RewardType.BUNDLE, item.value)
				: null,
		[item],
	);

	const lootboxName = useMemo(() => {
		if (!lootboxId) return '';
		return lootboxes.find((x) => x.id === lootboxId)?.name || lootboxId;
	}, [lootboxId, lootboxes]);

	const bundleName = useMemo(() => {
		if (!bundleId) return '';
		return bundles.find((x) => x.id === bundleId)?.name || bundleId;
	}, [bundleId, bundles]);

	const itemName = useMemo(() => {
		if (!parsedItem) return '';
		return (
			inventoryItems.find((x) => x.id === parsedItem.id)?.name || parsedItem.id
		);
	}, [parsedItem, inventoryItems]);

	const hintText = useMemo(() => {
		switch (item.type) {
			case RewardType.MONEY:
			case RewardType.EXP:
				return parseInt(item.value).toLocaleString('ru-RU');
			case RewardType.VEHICLE:
				return parsedVehicle?.model ?? item.value;
			case RewardType.LOOTBOX:
				return lootboxName;
			case RewardType.BUNDLE:
				return bundleName;
			case RewardType.ITEM:
				if (!parsedItem) return item.value;
				return parsedItem.count && parsedItem.count > 1
					? `${itemName} ×${parsedItem.count}`
					: itemName;
			default:
				return item.value;
		}
	}, [item, parsedVehicle, parsedItem, lootboxName, bundleName, itemName]);

	const renderImage = () => {
		switch (item.type) {
			case RewardType.VEHICLE:
				if (!parsedVehicle?.model) return null;
				return (
					<CollectionImage
						collection="vehicles"
						id={parsedVehicle.model as never}
						size={collectionSize}
					/>
				);
			case RewardType.LOOTBOX:
				if (!lootboxId) return null;
				return (
					<CollectionImage
						collection="cases"
						id={lootboxId as never}
						size={collectionSize}
					/>
				);
			case RewardType.BUNDLE:
				if (!bundleId) return null;
				return (
					<CollectionImage
						collection="kits"
						id={bundleId as never}
						size={collectionSize}
					/>
				);
			case RewardType.ITEM:
				if (!parsedItem?.id) return null;
				if (isClothesItem(parsedItem.id)) {
					return <ClothesImage id={parsedItem.id} size={collectionSize} />;
				}
				return (
					<CollectionImage
						collection="items"
						id={parsedItem.id as never}
						size={collectionSize}
					/>
				);
			case RewardType.MONEY:
				return <img src={cardSoft} width={iconSize} height={iconSize} />;
			case RewardType.EXP:
				return <img src={starExp} width={iconSize} height={iconSize} />;
			default:
				return null;
		}
	};

	return (
		<StyledContainer $rowSpan={rowSpan} $colSpan={colSpan}>
			<StyledShadow />
			<StyledItemContainer
				onMouseEnter={() => setHintVisible(true)}
				onMouseLeave={() => setHintVisible(false)}
				$bigImage={isBig}
			>
				{renderImage()}
			</StyledItemContainer>
			<CSSTransition
				timeout={300}
				classNames={'hint'}
				in={hintVisible}
				unmountOnExit
			>
				<StyledHintContainer>
					<TextLabel>{hintText}</TextLabel>
					{item.type === RewardType.MONEY && (
						<CurrencyIcon icon={rubleIcon} size="sm" />
					)}
					{item.type === RewardType.EXP && <img src={starExp} />}
				</StyledHintContainer>
			</CSSTransition>
		</StyledContainer>
	);
};

export { QuestPromocodeItem };
